<template>
    <div>
        <div
            class="w-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mb-5"
            v-if="institution.cards.length > 0"
        >
            <div
                class="text-sm border-b border-slate-200 rounded-t-lg bg-gray-50 py-2.5 pl-4 pr-3 flex justify-between items-center"
                :class="{
                    'border-b border-slate-200': !isCollapsed,
                }"
            >
                <span class="text-sm font-semibold flex items-center">
                    <img
                        :src="institution.small_logo_url"
                        :alt="institution.issuing_bank.name"
                        class="inline mr-2 lg:mr-5 w-5 lg:w-10 h-auto rounded-full"
                    />
                    {{ institution.issuing_bank.name }}
                    <span class="font-light ml-1">
                        ({{ institution.cards.length }})
                    </span>

                    <a
                        href="/wallet/members"
                        v-if="hasMemberError"
                        class="flex items-center group"
                    >
                        <span
                            class="rounded-full w-7 h-7 bg-amber-100 border border-1 border-amber-200 flex items-center justify-center ml-2 group-hover:border-amber-400"
                        >
                            <i class="far fa-warning text-amber-600"></i>
                        </span>
                        <span
                            class="font-light text-amber-600 text-xs ml-1 group-hover:underline"
                        >
                            (Click here to address an error with your
                            {{ institution.issuing_bank.name }}
                            connection.)
                        </span>
                    </a>
                </span>
                <button
                    @click="toggleCollapse"
                    class="btn-icon btn-transparent"
                >
                    <Icon
                        :icon="isCollapsed ? 'chevron-down' : 'chevron-up'"
                        class="text-gray-400 transition-all duration-300"
                    />
                </button>
            </div>
            <Transition name="list-container" tag="div">
                <div class="collapse-row" v-show="!isCollapsed">
                    <div class="hidden xl:grid grid-cols-1 gap-4">
                        <div
                            class="relative flex items-center space-x-3 border-b border-slate-200 bg-white px-3 lg:px-6 py-3 shadow-sm"
                        >
                            <div class="min-w-[40%]">
                                <span class="text-sm font-semibold">
                                    Card Name
                                </span>
                            </div>
                            <div class="min-w-[40%]">
                                <div
                                    class="flex flex-wrap text-center justify-start"
                                >
                                    <span
                                        class="text-sm font-semibold px-1 w-1/3 flex items-center justify-end"
                                    >
                                        Card Status
                                        <SortDirection
                                            :direction="sortDirection"
                                            activeSort="card_status"
                                            :column="sortColumn"
                                            @sort-changed="handleSort"
                                        />
                                    </span>
                                    <span
                                        class="text-sm font-semibold px-1 w-1/3 flex items-center justify-end"
                                    >
                                        Date Opened
                                        <SortDirection
                                            :direction="sortDirection"
                                            activeSort="date_opened"
                                            :column="sortColumn"
                                            @sort-changed="handleSort"
                                        />
                                    </span>
                                    <span
                                        class="text-sm font-semibold px-1 w-1/3 flex items-center justify-end"
                                    >
                                        Annual Fee
                                        <SortDirection
                                            :direction="sortDirection"
                                            activeSort="annual_fee"
                                            :column="sortColumn"
                                            @sort-changed="handleSort"
                                        />
                                    </span>
                                </div>
                            </div>
                            <div class="min-w-[19%]"></div>
                        </div>
                    </div>
                    <!-- start indiv card -->
                    <TransitionGroup
                        name="list"
                        tag="div"
                        class="divide-y divide-slate-200 rounded-b-lg"
                    >
                        <div
                            class="grid grid-cols-1 gap-4 rounded-b-lg"
                            v-for="item in sortedCards"
                            :key="'ca-' + item.id"
                        >
                            <div
                                class="relative flex flex-wrap items-start lg:space-x-3 px-3 lg:px-6 lg:!pr-0 py-3 shadow-sm rounded-b-lg"
                                :class="{
                                    unmatched: item.unmatched,
                                    'bg-gray-100': item.ignore,
                                    'bg-white': !item.ignore,
                                }"
                            >
                                <div
                                    class="min-w-[100%] w-[100%] ml-0 mb-2 xl:mb-0 xl:min-w-0 xl:w-[40%]"
                                >
                                    <span>
                                        <img
                                            :src="`${cdnUrl}/${item.card.card_image.path}/_small/${item.card.card_image.file_name}`"
                                            v-if="
                                                item.card &&
                                                item.card.card_image
                                            "
                                            class="mr-3 float-left max-w-[50px] lg:max-w-[80px] h-auto"
                                        />
                                        <img
                                            :src="
                                                item.institution.medium_logo_url
                                            "
                                            v-if="item.card && !item.card.image"
                                            class="mr-3 float-left"
                                            style="
                                                max-width: 60px;
                                                height: auto;
                                            "
                                        />
                                        <img
                                            src="/images/blank_card.png"
                                            v-if="
                                                !item.card &&
                                                item.card_match_request
                                            "
                                            class="mr-3 float-left max-w-[50px] lg:max-w-[80px] h-auto"
                                        />
                                    </span>
                                    <div class="flex flex-col">
                                        <div
                                            class="text-xs 2xl:text-sm text-gray-800 relative"
                                        >
                                            <div class="items-center">
                                                <div>
                                                    <span
                                                        v-if="
                                                            !item.card &&
                                                            item.card_match_request
                                                        "
                                                        class="text-sm font-semibold leading-tight"
                                                    >
                                                        {{
                                                            item.card_not_found_bank
                                                        }}
                                                        {{
                                                            item.card_not_found_name
                                                        }}
                                                    </span>
                                                </div>
                                                <div
                                                    v-if="
                                                        item.card_match_request &&
                                                        !item.card_match_request
                                                            .resolved &&
                                                        !item.card_match_request
                                                            .rejected_reason
                                                    "
                                                    class="mb-2 py-2 px-4 bg-yellow-50 border border-yellow-200 rounded-md"
                                                >
                                                    <p
                                                        class="text-sm text-yellow-700"
                                                    >
                                                        This card is awaiting a
                                                        missing card request
                                                        match
                                                    </p>
                                                </div>
                                                <div
                                                    v-if="
                                                        item.card_match_request &&
                                                        !item.card_match_request
                                                            .resolved &&
                                                        item.card_match_request
                                                            .rejected
                                                    "
                                                    class="mb-2 py-2 px-4 bg-red-50 border border-red-200 rounded-md"
                                                >
                                                    <p
                                                        class="text-sm text-red-700"
                                                    >
                                                        This card match request
                                                        was rejected for the
                                                        following reason:
                                                        <br />
                                                        "{{
                                                            item
                                                                .card_match_request
                                                                .rejected_reason
                                                        }}"
                                                    </p>
                                                </div>
                                                <span
                                                    v-if="
                                                        item.card &&
                                                        !item.card_match_request
                                                    "
                                                    class="text-sm font-semibold 2xl:font-normal leading-tight"
                                                >
                                                    {{ item.card.title }}
                                                </span>
                                                <span
                                                    v-if="
                                                        !item.card &&
                                                        !item.card_match_request
                                                    "
                                                    class="text-sm font-semibold 2xl:text-base 2xl:font-normal leading-tight"
                                                >
                                                    {{ item.name }}
                                                </span>
                                                <span
                                                    class="text-xs ml-1"
                                                    v-if="
                                                        item.last_four &&
                                                        !item.card_match_request &&
                                                        userSettings.show_last_four
                                                    "
                                                >
                                                    ({{ item.last_four }})
                                                </span>
                                                <span
                                                    class="inline-flex items-center justify-center rounded-full bg-gray-100 !text-gray-600 text-xs w-4 h-4 lg:w-6 lg:h-6 relative annual-fee-label-trigger cursor-pointer ml-2"
                                                    v-if="
                                                        item.member_guid &&
                                                        !item.card_match_request
                                                    "
                                                >
                                                    <span
                                                        class="annual-fee-label text-xs"
                                                    >
                                                        This product is
                                                        connected to your
                                                        {{
                                                            item.institution
                                                                .issuing_bank
                                                                .name
                                                        }}
                                                        account.
                                                    </span>
                                                    <i
                                                        class="fa-regular fa-link-simple !text-gray-600 text-[.75em] sm:text-[0.875em] sm:leading-[0.0714285718em] sm:align-[0.0535714295em]"
                                                    ></i>
                                                </span>
                                                <span
                                                    class="inline-flex items-center justify-center rounded-full bg-gray-100 !text-gray-600 text-xs w-4 h-4 lg:w-6 lg:h-6 relative annual-fee-label-trigger cursor-pointer ml-2"
                                                    v-if="
                                                        item &&
                                                        item.card &&
                                                        item.account_type ===
                                                            'authorized_user'
                                                    "
                                                >
                                                    <span
                                                        class="annual-fee-label text-xs"
                                                    >
                                                        You are an authorized
                                                        user on the
                                                        {{ item.card.title }}.
                                                    </span>
                                                    <i
                                                        class="fas fa-user-friends !text-gray-600 text-[.75em] sm:text-[0.875em] sm:leading-[0.0714285718em] sm:align-[0.0535714295em]"
                                                    ></i>
                                                </span>

                                                <span
                                                    class="inline-flex items-center justify-center rounded-full bg-gray-100 !text-gray-600 text-xs w-4 h-4 lg:w-6 lg:h-6 relative annual-fee-label-trigger cursor-pointer ml-2"
                                                    v-if="
                                                        item &&
                                                        item.product_changed
                                                    "
                                                >
                                                    <span
                                                        class="annual-fee-label text-xs"
                                                        v-if="
                                                            item.original_card
                                                        "
                                                    >
                                                        You product changed this
                                                        from a
                                                        {{
                                                            item.original_card
                                                                .title
                                                        }}.
                                                    </span>
                                                    <i
                                                        class="fa-solid fa-arrows-repeat !text-gray-600 text-[.75em] sm:text-[0.875em] sm:leading-[0.0714285718em] sm:align-[0.0535714295em]"
                                                    ></i>
                                                </span>

                                                <div
                                                    class="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-600 ml-2 annual-fee-label-trigger relative cursor-pointer"
                                                    v-if="item.is_new_account"
                                                >
                                                    New Card
                                                    <span
                                                        class="annual-fee-label text-xs"
                                                        v-if="
                                                            item.is_new_account
                                                        "
                                                    >
                                                        Congratulations on your
                                                        new
                                                        <span
                                                            v-if="
                                                                item.card &&
                                                                item.card.title
                                                            "
                                                        >
                                                            {{
                                                                item.card.title
                                                            }}
                                                        </span>
                                                        <span v-else>card</span>
                                                        . We recently imported
                                                        this card.
                                                        <span
                                                            v-if="
                                                                item.unmatched
                                                            "
                                                        >
                                                            Please be sure to
                                                            identify it and
                                                        </span>
                                                        <span
                                                            v-if="
                                                                !item.unmatched
                                                            "
                                                        >
                                                            Please
                                                        </span>
                                                        fill out all of your
                                                        account details.
                                                    </span>
                                                    <i
                                                        class="fa-regular fa-circle-exclamation ml-1 text-blue-400"
                                                    ></i>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            class="text-xs text-gray-600"
                                            v-if="item.credit_limit"
                                        >
                                            Credit Limit:
                                            {{
                                                $filters.formatMoney(
                                                    item.credit_limit
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="row-content-container min-w-[100%] lg:min-w-[80%] xl:min-w-0 xl:w-[40%] relative"
                                >
                                    <div
                                        class="grid grid-cols-12 gap-y-3 gap-x-4 items-start xl:flex xl:flex-wrap xl:text-center lg:justify-start xl:gap-0"
                                    >
                                        <div
                                            class="flex flex-col xl:w-1/3 xl:mb-3 xl:justify-center order-3 md:order-1 col-span-3"
                                        >
                                            <div
                                                class="md:grid gap-1 gap-y-2 md:grid-cols-1 text-left md:text-center 2xl:flex 2xl:text-center xl:justify-start 2xl:justify-center w-full"
                                            >
                                                <span
                                                    class="text-xs hidden md:block font-semibold xl:hidden w-full"
                                                >
                                                    Card Status:
                                                </span>
                                                <div
                                                    class="w-full md:text-center"
                                                >
                                                    <span
                                                        class="inline-flex items-center px-3 py-0.5 text-sm font-medium rounded-full text-xs lg:text-sm font-medium bg-green-100 text-green-700 capitalize -translate-y-[5px] xl:transform-none"
                                                        v-if="
                                                            item.card_status ===
                                                                'open' &&
                                                            !item.unmatched
                                                        "
                                                    >
                                                        Open
                                                    </span>

                                                    <span
                                                        class="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 text-xs lg:text-sm font-medium text-red-700 lg:mb-1 -translate-y-[5px] xl:transform-none"
                                                        v-if="
                                                            item.card_status ===
                                                                'closed' &&
                                                            !item.unmatched
                                                        "
                                                    >
                                                        Closed
                                                    </span>

                                                    <span
                                                        class="block text-xs -translate-y-[3px] xl:transform-none hidden xl:block"
                                                        v-if="
                                                            item.card_status ===
                                                                'closed' &&
                                                            !item.unmatched &&
                                                            item.date_closed
                                                        "
                                                    >
                                                        ({{ item.date_closed }})
                                                    </span>
                                                </div>
                                            </div>
                                            <a
                                                href="#"
                                                role="button"
                                                v-if="item.unmatched"
                                                @click.prevent="
                                                    openEditCardModal({
                                                        card: item,
                                                        user: user,
                                                    })
                                                "
                                                class="btn btn-sm btn-primary text-white ml-auto mr-auto relative annual-fee-label-trigger"
                                            >
                                                <span
                                                    class="annual-fee-label text-xs"
                                                >
                                                    To be able to fully use
                                                    My10x, we need to know which
                                                    card product this is.
                                                </span>
                                                {{
                                                    item.card_match_request &&
                                                    !item.card_match_request
                                                        .resolved
                                                        ? 'Update Card Match'
                                                        : 'Match Card'
                                                }}
                                            </a>
                                        </div>
                                        <div
                                            class="flex flex-row xl:w-1/3 xl:mb-3 xl:justify-center order-1 md:order-2 col-span-6 md:col-span-3"
                                        >
                                            <div
                                                class="date-opened flex gap-1 text-left md:flex-col md:text-center xl:justify-start 2xl:justify-center w-full lg:mx-3"
                                            >
                                                <!-- "date-opened grid grid-cols-12 gap-1 text-left md:flex md:flex-col md:text-center xl:justify-start 2xl:justify-center w-full lg:mx-3" -->
                                                <span
                                                    class="text-xs font-semibold xl:hidden col-span-6 md:col-span-12 whitespace-nowrap"
                                                >
                                                    Date Opened:
                                                </span>
                                                <span
                                                    class="date-span ml-2 xl:ml-0 text-xs xl:text-sm xl:leading-none col-span-6 md:col-span-12"
                                                    v-if="
                                                        item.date_opened &&
                                                        !item.opened_long_ago &&
                                                        !item.product_changed
                                                    "
                                                >
                                                    {{ item.date_opened }}
                                                </span>
                                                <span
                                                    class="flex xl:items-center justify-end md:justify-center ml-2 xl:ml-0 text-xs xl:text-sm xl:leading-none col-span-7 md:col-span-12"
                                                    v-if="
                                                        item.product_changed &&
                                                        item.product_changed_date_opened
                                                    "
                                                >
                                                    {{
                                                        item.product_changed_date_opened
                                                    }}

                                                    <span
                                                        class="product-changed-container flex items-center justify-center rounded-full bg-gray-100 !text-gray-600 text-xs w-4 h-4 lg:w-6 lg:h-6 relative annual-fee-label-trigger cursor-pointer ml-3"
                                                        v-if="
                                                            item &&
                                                            item.product_changed &&
                                                            item.original_card
                                                        "
                                                    >
                                                        <Tooltip
                                                            :icon="'fa-arrows-repeat'"
                                                            :text="`You product changed
                                                            this from a
                                                            ${item.original_card.title}
                                                            on
                                                            ${item.product_changed_date}, and this is the
                                                            original date that
                                                            product was opened.`"
                                                        />
                                                    </span>
                                                </span>

                                                <span
                                                    v-if="
                                                        !item.opened_long_ago &&
                                                        !item.date_opened
                                                    "
                                                    class="inline-block rounded-full text-gray-400 text-center relative annual-fee-label-trigger cursor-pointer ml-1"
                                                >
                                                    <span
                                                        class="annual-fee-label text-xs"
                                                        v-if="item.card"
                                                    >
                                                        Please assign the date
                                                        that you opened this
                                                        card so that you can
                                                        fully use My10x.
                                                    </span>
                                                    <i
                                                        class="fa-regular fa-circle-exclamation"
                                                    ></i>
                                                </span>
                                                <span
                                                    class="text-xs xl:text-sm lg:leading-none ml-2 lg:ml-0 col-span-7 md:col-span-12"
                                                    v-if="item.opened_long_ago"
                                                >
                                                    > 2yrs ago
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            class="flex flex-row xl:w-1/3 xl:mb-3 xl:justify-center order-2 md:order-3 col-span-6 md:col-span-3 max-xs:ml-3.5"
                                        >
                                            <annual-fee :item="item" />
                                        </div>
                                        <div
                                            v-if="
                                                item.contributes_to_wallet_five_twenty_four
                                            "
                                            class="flex flex-row xl:mb-3 xl:justify-start xl:absolute 2xl:items-center order-4 col-span-3 md:col-span-3 xl:left-[100%]"
                                        >
                                            <div
                                                class="5/24-container md:grid-cols-2 lg:grid-cols-1 gap-1 text-left md:flex md:flex-col md:text-center xl:justify-start w-full whitespace-nowrap inline"
                                            >
                                                <span
                                                    class="text-xs hidden md:block font-semibold xl:font-medium xl:text-sm xl:hidden md:text-left"
                                                >
                                                    5/24 Count
                                                </span>
                                                <div
                                                    class="tooltip-container flex"
                                                >
                                                    <div
                                                        class="h-full pill !mr-1 !bg-gray-100 !text-gray-600 !px-3 !py-0.5 !font-medium !text-xs !lg:text-sm leading-none xl:ml-0 -translate-y-[4px] xl:transform-none"
                                                    >
                                                        {{
                                                            item.five_twenty_four_level
                                                        }}/24
                                                    </div>
                                                    <Tooltip
                                                        :text="`Your ${item.card.title} will fall out of your 5/24 count on approximately ${item.five_twenty_four_fall_off_date}.`"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="see-more-container !ml-0 min-w-0 w-[18%] text-right absolute bottom-2 right-2 lg:relative lg:right-auto lg:bottom-auto"
                                >
                                    <div
                                        class="flex items-center space-between"
                                    >
                                        <CardDropdown
                                            class="ml-auto"
                                            :item="item"
                                            @edit-card="
                                                openEditCardModal({
                                                    card: item,
                                                    user: user,
                                                })
                                            "
                                            @copy-card="
                                                openCopyCardModal({
                                                    account: item,
                                                    user,
                                                })
                                            "
                                            @delete-card="
                                                walletStore.deleteCard(item)
                                            "
                                        />
                                        <label
                                            class="ml-3 lg:ml-5 cursor-pointer lg:px-2 rounded-full w-8 lg:w-10 h-8 lg:h-10 flex items-center justify-center hover:bg-gray-100 outline-none"
                                            v-if="!item.unmatched"
                                        >
                                            <input
                                                type="checkbox"
                                                class="hidden"
                                                @change="toggleSidebar(item)"
                                            />
                                            <i
                                                class="far fa-angle-right fa-fw text-base lg:text-xl text-gray-400"
                                                :class="{
                                                    'fa-angle-right':
                                                        !item.sidebarOpen,
                                                    'fa-close':
                                                        item.sidebarOpen,
                                                }"
                                                style="
                                                    display: inline-block;
                                                    transition: transform 0.25s
                                                        ease;
                                                "
                                            ></i>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TransitionGroup>
                    <div
                        v-show="hiddenAccounts > 0"
                        class="flex py-3 px-6 rounded-b-lg"
                    >
                        <button
                            v-if="!showHiddenCards"
                            @click.prevent="setShowHiddenCards(true)"
                            class="flex gap-2 items-center text-gray-400 hover:text-gray-500 font-medium text-sm active:border-slate-300 focus:outline-none"
                            role="button"
                            id="hs-wallet-show-hidden-cards"
                        >
                            <i class="fa-regular fa-eye"></i>
                            <span>
                                Show {{ hiddenAccounts }} hidden card{{
                                    hiddenAccounts > 1 ? 's' : ''
                                }}
                            </span>
                        </button>
                        <button
                            v-else
                            @click.prevent="handleCollapse"
                            class="items-center text-gray-400 hover:text-gray-500 font-medium text-sm active:border-slate-300 focus:outline-none"
                            role="button"
                            id="hs-wallet-hide-hidden-cards"
                        >
                            <span class="flex gap-2 items-center">
                                <i class="fa-regular fa-eye-slash"></i>

                                Collapse {{ hiddenAccounts }} Hidden Cards
                            </span>
                        </button>
                    </div>
                    <!-- end indiv card -->
                </div>
            </Transition>
        </div>
    </div>
</template>

<script>
import { computed, watch, ref } from 'vue';
import { useAppStore } from '@/store/app.js';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import AnnualFee from '@/components/wallet/annual-fee.vue';
import CardDropdown from '@/dropdowns/card-dropdown.vue';
import SortDirection from '@/components/ui/sort-direction/sort-direction.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';

export default {
    components: {
        AnnualFee,
        CardDropdown,
        Icon,
        SortDirection,
        Tooltip,
    },
    props: {
        institution: Object,
        badMembers: Array,
        collapsedOnLoad: Boolean,
    },
    emits: ['toggleCollapse'],
    setup(props, { emit }) {
        const appStore = useAppStore();
        const walletStore = useWalletStore();
        const isCollapsed = ref(false);
        const showEditCardModal = ref(false);
        const sortDirection = ref('desc');
        const sortColumn = ref('card_status');
        const userSettings = computed(() => appStore.userSettings);
        const showHiddenCards = ref(false);
        const { filters } = storeToRefs(walletStore);
        const cdnUrl = ref(window.cdnUrl);

        const toggleCollapse = () => {
            isCollapsed.value = !isCollapsed.value;
            emit('toggleCollapse', props.institution.id);
        };

        const hasMemberError = computed(() => {
            return props.badMembers.some(
                (member) => member.institution_code === props.institution.code
            );
        });

        if (props.collapsedOnLoad) {
            isCollapsed.value = true;
        }

        const getSortValue = (card, column) => {
            if (column === 'annual_fee') {
                return card.user_managed_annual_fee
                    ? card.annual_fee
                    : card.card.annual_fee;
            } else if (column === 'date_opened') {
                return new Date(card[column]);
            }
            return card[column];
        };

        const hiddenAccounts = computed(() => {
            return props.institution.cards.filter(
                (accountObject) => accountObject.ignore
            ).length;
        });

        const sortedCards = computed(() => {
            const cardsCopy = [...props.institution.cards];
            cardsCopy.sort((a, b) => {
                const valA = getSortValue(a, sortColumn.value);
                const valB = getSortValue(b, sortColumn.value);
                if (sortColumn.value !== 'annual_fee') {
                    return valA < valB ? -1 : valA > valB ? 1 : 0;
                } else {
                    return valA - valB;
                }
            });
            const sorted =
                sortDirection.value === 'desc'
                    ? cardsCopy.reverse()
                    : cardsCopy;
            if (!showHiddenCards.value && !filters.value.ignore) {
                return sorted.filter((card) => !card.ignore);
            } else {
                return sorted.sort((a, b) => {
                    if (a.ignore && !b.ignore) {
                        return 1;
                    } else if (!a.ignore && b.ignore) {
                        return -1;
                    } else {
                        return 0;
                    }
                });
            }
        });

        const handleSort = (direction, columnName) => {
            sortDirection.value = direction;
            sortColumn.value = columnName;
        };

        const openEditCardModal = (obj) => {
            // Pre-fill the issuing bank for any card match request (new, open, or rejected)
            if (obj.card.card_match_request) {
                obj.card.card_not_found = true;
                obj.card.card_not_found_name =
                    obj.card.card_match_request.card_not_found_name;
                obj.card.card_not_found_bank =
                    obj.card.card_match_request.issuing_bank;
            }
            walletStore.setSelectedCard(obj.card);
            walletStore.setSelectedUser(obj.user);
            walletStore.setShowEditCardModal(true);
        };

        const openCopyCardModal = (obj, user) => {
            walletStore.setSelectedCard(obj.account);
            walletStore.setSelectedUser(user);
            walletStore.setShowCopyCardModal(true);
        };

        const updateCardData = () => {
            walletStore.getWallet();
        };

        const closeModal = () => {
            walletStore.setShowEditCardModal(false);
            walletStore.setSelectedCard(null);
            walletStore.setSelectedUser(null);
        };

        const toggleSidebar = (item) => {
            let show = item === walletStore.activeAccount ? false : true;
            walletStore.activeAccount.sidebarOpen = false;

            let setItem = item === walletStore.activeAccount ? {} : item;
            item.sidebarOpen = show;
            setItem.sidebarOpen = show;

            if (show) {
                walletStore.setActiveAccount(setItem);
                walletStore.setAccountSidebar(show);
            } else {
                walletStore.setAccountSidebar(show);
            }
        };

        const setShowHiddenCards = (val) => {
            showHiddenCards.value = val;
        };

        const handleCollapse = () => {
            if (filters.value.ignore) {
                isCollapsed.value = true;
            } else {
                setShowHiddenCards(false);
            }
        };

        watch(
            () => filters.value.ignore,
            (newVal) => {
                if (newVal !== showHiddenCards.value) {
                    setShowHiddenCards(newVal);
                }
            },
            { deep: true }
        );

        return {
            closeModal,
            filters,
            handleCollapse,
            handleSort,
            hasMemberError,
            hiddenAccounts,
            isCollapsed,
            openCopyCardModal,
            openEditCardModal,
            setShowHiddenCards,
            showEditCardModal,
            showHiddenCards,
            sortColumn,
            sortedCards,
            sortDirection,
            toggleCollapse,
            toggleSidebar,
            updateCardData,
            userSettings,
            walletStore,
            cdnUrl,
        };
    },
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.account-sidebar-open {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(-448px);
}
.account-sidebar-closed {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(0);
}
.annual-fee-label-trigger {
    &:hover {
        .annual-fee-label {
            display: block;
        }
    }
}
.annual-fee-label {
    display: none;
    width: 200px;
    padding: 6px 12px;
    color: #fff;
    background-color: #1c293d;
    border-radius: 4px;
    position: absolute;
    bottom: 110%;
    left: 50%;
    margin-left: -100px;
}

.annual-fee-label-trigger {
    &:hover {
        .annual-fee-label {
            display: block;
        }
    }
}
.annual-fee-label {
    display: none;
    width: 200px;
    padding: 6px 12px;
    color: #fff;
    background-color: #1c293d;
    border-radius: 4px;
    position: absolute;
    bottom: 110%;
    left: 50%;
    margin-left: -100px;
}
.unmatched {
    td {
        background-color: color.adjust(#f8ff73, $lightness: 22%);
    }
}
/*https://codepen.io/ahebler/pen/YXJJWb*/

.tooltip-container .tooltip {
    @apply text-gray-400 xs:max-sm:-ml-1;
}
svg.animated-arrow {
    @apply w-[60px] h-[60px] lg:w-[100px] lg:h-[100px] rotate-[-10deg] lg:rotate-[33deg] -scale-x-100;
    margin: 0 auto;
    .draw-arrow {
        stroke-width: 3;
        stroke: #fd421d;
        fill: none;
        stroke-dasharray: 400;
        stroke-dashoffset: 400;
        animation-duration: 2s;
        animation-delay: 2s;
        animation-fill-mode: forwards;
        animation-name: draw;
        &.tail-1 {
            animation-delay: 2.5s;
        }
        &.tail-2 {
            animation-delay: 2.7s;
        }
    }
}
@keyframes draw {
    to {
        stroke-dashoffset: 0;
    }
}

.list-enter-active,
.list-leave-active {
    transition: max-height 0.5s ease;
    max-height: 100px;
    overflow: hidden;
}
.list-enter-from,
.list-leave-to {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}

.list-container-enter-active,
.list-container-leave-active {
    transition:
        max-height 0.5s ease,
        opacity 0.5s ease;
    max-height: 2000px;
    overflow: hidden;
}

.list-container-enter-from,
.list-container-leave-to {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
}
</style>
